import { useTheme } from "@mui/material/styles";

// Utility function to format price with appropriate denomination (K, L, Cr)
export function priceDenomination(price) {
    let formattedPrice;
    let suffix;

    if (price >= 1e7) {
        formattedPrice = price / 1e7;
        suffix = " Cr";
    } else if (price >= 1e5) {
        formattedPrice = price / 1e5;
        suffix = " L";
    } else if (price >= 1e3) {
        formattedPrice = price / 1e3;
        suffix = " K";
    } else {
        return price.toString();
    }

    formattedPrice = formattedPrice.toFixed(2);
    if (formattedPrice.endsWith(".00")) {
        formattedPrice = formattedPrice.slice(0, -3);
    }

    return formattedPrice + suffix;
}

// Utility function to format dates
export function formatDate(dateString) {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.toLocaleString("default", { month: "short" });
    return `${day} ${month}`;
}

// Hook for generating chart configurations
export function useChartGenerator() {
    const theme = useTheme();

    const colors = [
        theme.palette.graph.cyan,
        theme.palette.graph.yellow,
        theme.palette.graph.magenta,
        theme.palette.graph.purple,
        theme.palette.graph.pink,
        theme.palette.graph.orange,
        theme.palette.graph.green,
    ];

    return (
        data,
        keyNames,
        xKey,
        { x, y, title, xFormatter, yFormatter } = {},
        type = "bar",
        stacked = false,
        yOverlap = [],
        isCategoryX = false
    ) => {
        return {
            series: keyNames.map((k) => ({
                data: data.map((i) => i[k.key] ?? 0),
                name: k.name,
                type: k.type,
            })),
            options: {
                chart: {
                    type: type,
                    height: 350,
                    stacked: stacked,
                },
                plotOptions: {
                    bar: {
                        endingShape: "rounded",
                        dataLabels: {
                            position: "top",
                        },
                    },
                },
                dataLabels: {
                    enabled: false,
                    formatter: function (val) {
                        return priceDenomination(val);
                    },
                    offsetY: -20,
                    style: {
                        fontSize: "12px",
                        colors: ["#304758"],
                    },
                },
                xaxis: {
                    categories: isCategoryX
                        ? data.map((i) => i[xKey])
                        : data.map(
                              (i) =>
                                  xFormatter?.(i[xKey]) || formatDate(i[xKey])
                          ),
                    title: {
                        text: x,
                    },
                    labels: {
                        rotate: -45,
                        rotateAlways: true,
                        maxHeight: 60,
                        formatter: function (value) {
                            return isCategoryX || xFormatter
                                ? value
                                : formatDate(value);
                        },
                    },
                },
                stroke: {
                    curve: "smooth",
                    ...(type === "line" && {
                        width: 2,
                    }),
                },
                markers: {
                    size: 5,
                    strokeColors: theme.palette.background.paper,
                    strokeWidth: 2,
                    colors: theme.palette.graph.cyan,
                },
                ...(yOverlap.length > 0
                    ? {
                          yaxis: yOverlap.map((y, i) => ({
                              seriesName: y.name,
                              opposite: y.opposite,
                              title: {
                                  text: y.name,
                                  style: {
                                      color: colors[i % colors.length],
                                  },
                              },
                              labels: {
                                  formatter: function (val) {
                                      return yFormatter
                                          ? yFormatter(val)
                                          : priceDenomination(val);
                                  },
                                  style: {
                                      colors: colors[i % colors.length],
                                      fontWeight: 700,
                                  },
                              },
                          })),
                      }
                    : {
                          yaxis: {
                              title: {
                                  text: y,
                              },
                              labels: {
                                  formatter: function (val) {
                                      return yFormatter
                                          ? yFormatter(val)
                                          : priceDenomination(val);
                                  },
                              },
                          },
                      }),
                title: {
                    text: title,
                    align: "left",
                },
                legend: {
                    position: "bottom",
                    horizontalAlign: "center",
                },
                colors,
            },
        };
    };
}
